import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Repeat, Sparkles, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useToast } from '@/components/ui/ToastProvider'
import { dateShort, money } from '@/lib/format'
import { cn } from '@/lib/utils'

interface RecurringGroup {
  id: string
  supplierTaxNumber: string
  supplierName: string | null
  cadence: 'monthly' | 'quarterly' | 'irregular'
  avgGross: number | null
  currency: string
  category: string | null
  vatCategory: string | null
  autoCategorize: boolean
  autoApprove: boolean
  autoApproveThresholdHuf: number | null
  lastSeenAt: string | null
  invoiceCount: number
}

function token() {
  return localStorage.getItem('adaptivo.szamla.token')
}

export function RecurringPage() {
  const { activeCompanyId } = useAuth()
  const toast = useToast()
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['recurring', activeCompanyId],
    queryFn: async () => {
      const t = token()
      const r = await fetch(`/api/v1/recurring?companyId=${activeCompanyId}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      })
      if (!r.ok) throw new Error(`Recurring lista: ${r.status}`)
      return (await r.json()) as { items: RecurringGroup[] }
    },
    enabled: !!activeCompanyId,
  })

  const scan = useMutation({
    mutationFn: async () => {
      const t = token()
      const r = await fetch(`/api/v1/recurring/scan?companyId=${activeCompanyId}`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      })
      if (!r.ok) throw new Error(`Scan: ${r.status}`)
      return (await r.json()) as { scanned: number; created: number; updated: number }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['recurring'] })
      toast.show({
        tone: 'success',
        title: 'Visszatérő-detektálás kész',
        description: `${data.scanned} szállító vizsgálva, ${data.created} új csoport.`,
      })
    },
    onError: (err) => {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : String(err) })
    },
  })

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<RecurringGroup> }) => {
      const t = token()
      const r = await fetch(`/api/v1/recurring/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(`Update: ${r.status}`)
      return r.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring'] }),
  })

  if (!activeCompanyId) return <p className="text-sm text-slate-500">Nincs aktív cég.</p>

  const items = list.data?.items ?? []

  return (
    <div>
      <PageHeader
        accent
        eyebrow="Automatizálás"
        title="Visszatérő számlák"
        subtitle="Felismert minta szerint havi/negyedéves szállítók — auto-kategorizálás és auto-jóváhagyás."
        actions={
          <Button onClick={() => scan.mutate()} loading={scan.isPending} leftIcon={<Repeat className="h-4 w-4" />}>
            Friss scan
          </Button>
        }
      />

      {list.isLoading ? (
        <CenterSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          variant="data"
          title="Még nincs visszatérő csoport felismerve"
          description="Kattints a Friss scan-re — a rendszer megnézi az utolsó 12 hónap számláit, és felismeri a havi/negyedéves szállítókat."
          action={<Button onClick={() => scan.mutate()} loading={scan.isPending}>Friss scan futtatása</Button>}
        />
      ) : (
        <div className="space-y-2">
          {items.map((g) => (
            <RecurringCard
              key={g.id}
              group={g}
              onUpdate={(body) => update.mutateAsync({ id: g.id, body })}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RecurringCard({ group, onUpdate }: { group: RecurringGroup; onUpdate: (b: Partial<RecurringGroup>) => Promise<unknown> }) {
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState(group.category ?? '')
  const [autoApproveThreshold, setAutoApproveThreshold] = useState(group.autoApproveThresholdHuf?.toString() ?? '')

  const cadenceLabel = {
    monthly: 'havi',
    quarterly: 'negyedéves',
    irregular: 'rendszertelen',
  }[group.cadence]

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/40" onClick={() => setOpen(!open)}>
        <Repeat className="h-4 w-4 text-brand-600 dark:text-brand-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{group.supplierName ?? group.supplierTaxNumber}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {cadenceLabel} · {group.invoiceCount} db · átlag {money(group.avgGross, group.currency)}
            {group.lastSeenAt && ` · utolsó: ${dateShort(group.lastSeenAt)}`}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {group.autoCategorize && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 font-medium">
              auto-kategória
            </span>
          )}
          {group.autoApprove && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-200 font-medium inline-flex items-center gap-1">
              <Sparkles className="h-2.5 w-2.5" />
              auto-OK ≤ {group.autoApproveThresholdHuf?.toLocaleString('hu-HU')}
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')} />
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-slate-50/50 dark:bg-slate-900/30">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Kategória</label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onBlur={() => category !== (group.category ?? '') && onUpdate({ category: category || null })}
              placeholder="pl. telefon, közmű"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">ÁFA-kategória (jelenleg)</label>
            <div className="text-sm text-slate-700 dark:text-slate-200 h-9 flex items-center px-3 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              {group.vatCategory ?? '—'}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={group.autoCategorize}
              onChange={(e) => onUpdate({ autoCategorize: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Auto-kategorizálás (kategória + ÁFA-kat. ráírja az új számlákra)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={group.autoApprove}
              onChange={(e) => onUpdate({ autoApprove: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Auto-jóváhagyás a limit alatt</span>
          </label>
          {group.autoApprove && (
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Auto-jóváhagyási limit (HUF) — ez alatt skip approval
              </label>
              <Input
                type="number"
                value={autoApproveThreshold}
                onChange={(e) => setAutoApproveThreshold(e.target.value)}
                onBlur={() =>
                  autoApproveThreshold !== (group.autoApproveThresholdHuf?.toString() ?? '') &&
                  onUpdate({ autoApproveThresholdHuf: autoApproveThreshold ? Number(autoApproveThreshold) : null })
                }
                placeholder="pl. 50000"
              />
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
